import React from 'react'
import { TrendingUp, TrendingDown, Shield, DollarSign, Users, Target, Clock, CheckCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface ExecutiveSummaryProps {
  timeRange: TimeRange
  compareEnabled: boolean
}

interface MetricCardData {
  title: string
  value: string
  change: string
  trend: 'up' | 'down' | 'stable'
  icon: React.ElementType
  color: string
  description?: string
  highlight?: boolean
}

const MetricCard: React.FC<MetricCardData> = ({ 
  title, 
  value, 
  change, 
  trend, 
  icon: Icon, 
  color,
  description,
  highlight = false 
}) => {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-3 w-3" />
    if (trend === 'down') return <TrendingDown className="h-3 w-3" />
    return null
  }

  const getTrendColor = () => {
    // For fraud-related metrics, down trend is good
    if (title.toLowerCase().includes('fraud') && trend === 'down') return 'text-green-600 dark:text-green-400'
    if (title.toLowerCase().includes('fraud') && trend === 'up') return 'text-red-600 dark:text-red-400'
    
    // For positive metrics (money saved, accuracy), up is good
    if (trend === 'up') return 'text-green-600 dark:text-green-400'
    if (trend === 'down') return 'text-red-600 dark:text-red-400'
    return 'text-gray-600 dark:text-gray-400'
  }

  return (
    <Card className={highlight ? 'ring-2 ring-primary/50 shadow-lg' : ''}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground mb-2">
              {title}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-foreground">
                {value}
              </span>
              <div className={`flex items-center gap-1 text-sm font-medium ${getTrendColor()}`}>
                {getTrendIcon()}
                {change}
              </div>
            </div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  timeRange,
  compareEnabled
}) => {
  // Mock data - in real app, this would come from API
  const metrics: MetricCardData[] = [
    {
      title: 'Total Leads Processed',
      value: '3,247',
      change: '+12.3%',
      trend: 'up',
      icon: Users,
      color: 'bg-blue-500',
      description: 'Last 30 days'
    },
    {
      title: 'Fraud Detected',
      value: '1,039 (32%)',
      change: '-5.2%',
      trend: 'down',
      icon: Shield,
      color: 'bg-red-500',
      description: 'Fraud rate decreased'
    },
    {
      title: 'Money Saved',
      value: '$31,170',
      change: '+18.7%',
      trend: 'up',
      icon: DollarSign,
      color: 'bg-green-500',
      description: 'Prevented fraud costs',
      highlight: true
    },
    {
      title: 'System Accuracy',
      value: '95.3%',
      change: '+2.1%',
      trend: 'up',
      icon: Target,
      color: 'bg-purple-500',
      description: 'Detection precision'
    },
    {
      title: 'Avg Response Time',
      value: '47ms',
      change: '-12ms',
      trend: 'down',
      icon: Clock,
      color: 'bg-orange-500',
      description: 'Processing speed'
    },
    {
      title: 'False Positives',
      value: '1.8%',
      change: '-0.3%',
      trend: 'down',
      icon: CheckCircle,
      color: 'bg-teal-500',
      description: 'Legitimate leads blocked'
    }
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Executive Summary</h2>
        {compareEnabled && (
          <Badge variant="outline" className="text-xs">
            Comparing to previous period
          </Badge>
        )}
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>
      
      {/* Additional Context */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">ROI</p>
                <p className="text-lg font-bold text-green-600">29,571%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded">
                <Shield className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Active Rules</p>
                <p className="text-lg font-bold text-blue-600">12</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Lead Sources</p>
                <p className="text-lg font-bold text-purple-600">8</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}